import { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import { Loader2, Save, CloudUpload, Clock, User, Euro } from 'lucide-react';

import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { UserAutocomplete } from '@/components/ui/user-autocomplete';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { useToast } from '@/components/ui/use-toast';
import { CollapsibleSection } from '@/components/ui/collapsible-section';
import { useTaxonomy, useValidFeatures, useValidStyles, useSubtypes } from '@/api/taxonomy';
import { useSaveEnrichment } from '@/api/enrichment';
import {
  classifyConflictCode,
  useClassifyLocation,
  useRefetchEnrichmentAfterClassification,
} from '@/api/synced-locations';
import { MultiSelectField } from './MultiSelectField';
import { TaxonomyTypeFields } from './TaxonomyTypeFields';
import { ParkingSelector } from './ParkingSelector';
import { PricingFields } from './PricingFields';
import { WorkHoursEditor } from './WorkHoursEditor';
import { UnifiedImageGrid } from '@/components/unified-gallery';
import { LocationTagsSection } from '@/components/tag-suggestions';
import { detectPropertyType } from '@/lib/detect-property-type';
import type { ClassificationBaseline, Enrichment, EnrichmentRequest, LocationImage, OwnerInfo, TagSuggestion, TagAnalysisStatus, TagRunUsage } from '@/types/api';

interface EnrichmentFormProps {
  syncedLocationId: string;
  existingEnrichment?: Enrichment;
  isPublished?: boolean;
  /**
   * Location name/title used for auto-detecting property category and type.
   */
  locationName?: string;
  /**
   * Partner's property type (e.g., "Apartment", "House") used as a fallback
   * for auto-detecting property category and type when the location name
   * doesn't match any keywords.
   */
  partnerPropertyType?: string | null;
  /**
   * Cancellation policy as provided by the partner (e.g., "flexible", "moderate", "strict").
   * Displayed as a hint to help admins choose the right ScoutAround cancellation policy.
   */
  partnerCancellationPolicy?: string | null;
  /**
   * Partner images for cover photo selection (FR-086, FR-087, FR-088).
   */
  partnerImages?: LocationImage[];
  /**
   * Callback when user clicks an image to view it full-size.
   */
  onImageView?: (index: number) => void;
  /**
   * Default owner from the partner account.
   * Used to show which owner will be used if no override is set.
   */
  partnerDefaultOwner?: OwnerInfo | null;
  /**
   * When true, prices are auto-calculated from partner pricing formula and cannot be edited.
   */
  pricingFormulaEnabled?: boolean;
  /**
   * Tag data for the unified image grid.
   */
  tagData?: {
    confirmedImageTags: Record<string, number[]>;
    /** Manually-added image tags (Phase 26) */
    manualImageTags?: Record<string, number[]>;
    imageSuggestions: Record<string, TagSuggestion[]>;
    /** The latest tag run of each photo (FR-142) */
    imageRuns?: Record<string, TagRunUsage>;
    allSuggestions: {
      location?: TagSuggestion[];
      images?: Record<string, TagSuggestion[]>;
    };
    /** Available tags for looking up manual tag names (Phase 26) */
    availableTags?: { id: number; name: string }[];
    rejectedTags: number[];
    processingTags: Set<number>;
    /** Tags currently being removed (Phase 26) */
    removingTags?: Set<number>;
  };
  /**
   * Handler for accepting an image tag.
   */
  onAcceptTag?: (tagId: number, imageIndex: number) => void;
  /**
   * Handler for rejecting an image tag.
   */
  onRejectTag?: (tagId: number, imageIndex: number) => void;
  /**
   * Handler for removing an image tag (Phase 26).
   */
  onRemoveTag?: (tagId: number, imageIndex: number) => void;
  /**
   * Current status of tag analysis. When 'analyzing', the form shows
   * an overlay to indicate the analysis is in progress.
   */
  tagAnalysisStatus?: TagAnalysisStatus;
  /**
   * Handler for re-analyzing all images.
   */
  onReanalyzeAllImages?: () => void;
  /**
   * Whether re-analyze all images is in progress.
   */
  isReanalyzingAllImages?: boolean;
  /**
   * Handler for re-analyzing a single image.
   */
  onReanalyzeSingleImage?: (imageIndex: number) => void;
  /**
   * Whether any single image re-analysis is in progress.
   */
  isReanalyzingSingleImage?: boolean;
  /**
   * Index of the image currently being re-analyzed (if any).
   */
  reanalyzingImageIndex?: number;
  /**
   * Handler for removing a partner image, called with the image's URL.
   * Offered by the page for file-import accounts only. The control is disabled
   * while the form has unsaved changes: a removal reloads the enrichment from
   * the server (its coverImageIndex may have moved), which would otherwise
   * either discard the edits or save a stale cover index over the remapped one.
   */
  onRemoveImage?: (imageUrl: string) => void;
  /**
   * Index of the image whose removal is in flight (if any). While it is set,
   * the whole gallery is disabled: every Remove, and every index-addressed
   * action (cover, tag accept/reject/add/remove, re-analyze), because the
   * removal's refetch shifts the indexes those actions would send. -1 when the
   * image had already left the gallery: the lock holds and no card spins.
   */
  removingImageIndex?: number;
  /**
   * Handler for removing several partner images at once, called with their
   * URLs in gallery order. Offered with onRemoveImage, under the same rules.
   */
  onRemoveImages?: (imageUrls: string[]) => void;
  /** Progress of a multi-photo removal in flight; removingImageIndex is -1 meanwhile. */
  bulkRemovalProgress?: { done: number; total: number };
  /**
   * Minimum booking hours configured for this partner type.
   * Defaults to 12 if not provided (backwards-compatible).
   */
  defaultMinimumBookingHours?: number;
  /**
   * Instant booking default configured for this partner type
   * (Guesty: true, WordPress/Podkarpackie catalogue: false).
   * Defaults to true if not provided (backwards-compatible).
   */
  defaultInstantBooking?: boolean;
  /**
   * True while a classification run (Classify with AI, or the classification batch) is choosing
   * this location's category, subtypes, styles and features. Threaded through to
   * `TaxonomyTypeFields` to advisorily disable the editor; its end reloads the enrichment.
   */
  isGeneratingType?: boolean;
  /** AI confidence (0-1) for the currently-assigned type, when AI-sourced. */
  typeAiConfidence?: number | null;
}

export function EnrichmentForm({
  syncedLocationId,
  existingEnrichment,
  isPublished = false,
  locationName,
  partnerPropertyType,
  partnerCancellationPolicy,
  partnerImages = [],
  onImageView,
  partnerDefaultOwner,
  pricingFormulaEnabled = false,
  tagData,
  onAcceptTag,
  onRejectTag,
  onRemoveTag,
  tagAnalysisStatus,
  onReanalyzeAllImages,
  isReanalyzingAllImages = false,
  onReanalyzeSingleImage,
  isReanalyzingSingleImage = false,
  reanalyzingImageIndex,
  onRemoveImage,
  removingImageIndex,
  onRemoveImages,
  bulkRemovalProgress,
  defaultMinimumBookingHours = 12,
  defaultInstantBooking = true,
  isGeneratingType,
  typeAiConfidence,
}: EnrichmentFormProps): JSX.Element {
  const { toast } = useToast();
  const { data: taxonomy, isLoading: isLoadingTaxonomy } = useTaxonomy();
  const saveEnrichment = useSaveEnrichment();
  const classifyLocation = useClassifyLocation();
  // A run writes into the enrichment this form loads from; reload it when the run ends.
  useRefetchEnrichmentAfterClassification(syncedLocationId, isGeneratingType);

  const [showConfirmDialog, setShowConfirmDialog] = useState(false);

  // Track if tags have been changed (confirmed, rejected, or manually added/removed)
  // This enables the save button so changes can be synced to ScoutAround
  const [tagsChanged, setTagsChanged] = useState(false);

  // A classification run writes its picks straight into the enrichment, and its end reloads the
  // form from them, so the form reads as unchanged. Save is still offered once a run has started
  // here or ended while the page watched it, so the result can be saved (and, on a published
  // location, synced) without first editing another field. Cleared by a save.
  const [classificationToSave, setClassificationToSave] = useState(false);
  const wasGeneratingTypeRef = useRef(isGeneratingType === true);
  useEffect(() => {
    const generating = isGeneratingType === true;
    if (wasGeneratingTypeRef.current && !generating) {
      setClassificationToSave(true);
    }
    wasGeneratingTypeRef.current = generating;
  }, [isGeneratingType]);

  // Track if a single-image reanalysis is in progress (persists until job completes)
  // This prevents showing the full-page overlay during single-image reanalysis
  const singleImageReanalysisInProgress = useRef(false);
  const previousTagAnalysisStatus = useRef(tagAnalysisStatus);

  // Owner override state (separate from formData since we need the full object for UserAutocomplete)
  const [ownerOverride, setOwnerOverride] = useState<OwnerInfo | null>(
    existingEnrichment?.owner ?? null
  );

  // Default work hours: every day 00:00 to 23:59
  const defaultWorkHours = useMemo<NonNullable<EnrichmentRequest['workHours']>>(() => [
    { dayOfWeek: 0, isOpen: true, openTime: '00:00', closeTime: '23:59' },
    { dayOfWeek: 1, isOpen: true, openTime: '00:00', closeTime: '23:59' },
    { dayOfWeek: 2, isOpen: true, openTime: '00:00', closeTime: '23:59' },
    { dayOfWeek: 3, isOpen: true, openTime: '00:00', closeTime: '23:59' },
    { dayOfWeek: 4, isOpen: true, openTime: '00:00', closeTime: '23:59' },
    { dayOfWeek: 5, isOpen: true, openTime: '00:00', closeTime: '23:59' },
    { dayOfWeek: 6, isOpen: true, openTime: '00:00', closeTime: '23:59' },
  ], []);

  // Find default cancellation policy ID ('Standard 90 days')
  const defaultCancellationPolicyId = useMemo(() => {
    const policies = taxonomy?.cancellationPolicies ?? [];
    // Look for exact match "Standard 90 days" in title (case-insensitive)
    const standard90 = policies.find(
      (p) => p.title.toLowerCase() === 'standard 90 days'
    );
    // Fallback: look for title containing "standard" and "90"
    const standard90Fallback = policies.find(
      (p) => p.title.toLowerCase().includes('standard') && p.title.includes('90')
    );
    // Second fallback: any policy with "standard" in title
    const standardPolicy = policies.find((p) => p.title.toLowerCase().includes('standard'));
    return standard90?.id ?? standard90Fallback?.id ?? standardPolicy?.id ?? policies[0]?.id ?? null;
  }, [taxonomy?.cancellationPolicies]);

  const getDefaultFormData = useCallback((): EnrichmentRequest => ({
    locationTypeId: null,
    subtypeIds: [],
    styles: [],
    features: [],
    activities: [],
    pricePerHour: null,
    pricePerDay: null,
    pricePerWeek: null,
    currency: 'EUR',
    maximumGuests: null, // Optional — no default crew cap (backend allows null; many open-air locations have no limit)
    instantBooking: defaultInstantBooking, // From partner type (catalogue partners default to false)
    // Phase 19: Complete Attribute Coverage
    rules: [],
    parking: [],
    workHours: defaultWorkHours,
    cancellationPolicyId: defaultCancellationPolicyId,
    minimumBookingHours: defaultMinimumBookingHours,
    accesses: [],
    // Phase 19: Cover Photo Selection (FR-086, FR-087, FR-088)
    coverImageIndex: null,
  }), [defaultWorkHours, defaultCancellationPolicyId, defaultMinimumBookingHours, defaultInstantBooking]);

  const [formData, setFormData] = useState<EnrichmentRequest>(getDefaultFormData);

  // Fetch valid feature and style IDs for the selected location type combination
  // These limit which features/styles can be synced to ScoutAround for this type
  const { data: validFeatureIds, isLoading: isLoadingValidFeatures } = useValidFeatures(
    formData.locationTypeId ?? null
  );
  const { data: validStyleIds, isLoading: isLoadingValidStyles } = useValidStyles(
    formData.locationTypeId ?? null
  );
  const { data: subtypes, isLoading: isLoadingSubtypes } = useSubtypes(formData.locationTypeId ?? null);

  // Track initial form state for pending changes detection
  const savedFormDataRef = useRef<EnrichmentRequest | null>(null);
  const savedOwnerOverrideRef = useRef<OwnerInfo | null>(null);
  // The classification values as loaded, before the automatic category pre-fill. A save sends
  // them, and the server keeps whatever the person left as loaded, so a classification run that
  // finished after this form loaded is not overwritten by it.
  const loadedClassificationRef = useRef<ClassificationBaseline>({
    locationTypeId: null,
    subtypeIds: [],
    styles: [],
    features: [],
  });
  // The category detectPropertyType pre-filled on a location that had none, or null.
  const detectedTypeIdRef = useRef<string | null>(null);

  // Initialize form with existing enrichment data or defaults
  useEffect(() => {
    let newFormData: EnrichmentRequest;

    if (existingEnrichment) {
      // Ensure minimumBookingHours meets partner type minimum
      const minHours = existingEnrichment.minimumBookingHours;
      const validMinHours = minHours != null && minHours >= defaultMinimumBookingHours ? minHours : defaultMinimumBookingHours;

      newFormData = {
        locationTypeId: existingEnrichment.locationTypeId,
        subtypeIds: existingEnrichment.subtypeIds ?? [],
        styles: existingEnrichment.styles ?? [],
        features: existingEnrichment.features ?? [],
        amenities: existingEnrichment.amenities ?? [],
        activities: existingEnrichment.activities ?? [],
        customTitle: existingEnrichment.customTitle,
        customDescription: existingEnrichment.customDescription,
        pricePerHour: existingEnrichment.pricePerHour,
        pricePerDay: existingEnrichment.pricePerDay,
        pricePerWeek: existingEnrichment.pricePerWeek,
        // Null stays null (the partner sent no currency): defaulting it to EUR here
        // would relabel the partner's prices on the next save of any field.
        currency: existingEnrichment.currency === undefined ? 'EUR' : existingEnrichment.currency,
        minimumBookingHours: validMinHours,
        maximumGuests: existingEnrichment.maximumGuests, // preserve null (no crew cap); do NOT mask as 50
        instantBooking: existingEnrichment.instantBooking ?? defaultInstantBooking,
        // Phase 19: Complete Attribute Coverage
        rules: existingEnrichment.rules ?? [],
        parking: existingEnrichment.parking ?? [],
        workHours: existingEnrichment.workHours?.length ? existingEnrichment.workHours : defaultWorkHours,
        cancellationPolicyId: existingEnrichment.cancellationPolicyId ?? defaultCancellationPolicyId,
        accesses: existingEnrichment.accesses ?? [],
        priceOnRequest: existingEnrichment.priceOnRequest ?? false,
        // Phase 19: Cover Photo Selection (FR-086, FR-087, FR-088)
        coverImageIndex: existingEnrichment.coverImageIndex ?? null,
      };
    } else {
      newFormData = getDefaultFormData();
    }

    loadedClassificationRef.current = {
      locationTypeId: newFormData.locationTypeId ?? null,
      subtypeIds: [...(newFormData.subtypeIds ?? [])],
      styles: [...(newFormData.styles ?? [])],
      features: [...(newFormData.features ?? [])],
    };
    detectedTypeIdRef.current = null;

    // Auto-detect property type when no type is selected yet (FR-008b)
    // Tries matching location name first, then falls back to partner property type
    if (!newFormData.locationTypeId && taxonomy?.locationTypes) {
      const textToMatch = locationName ?? partnerPropertyType;
      if (textToMatch) {
        const detected = detectPropertyType(locationName ?? '', { locationTypes: taxonomy.locationTypes }, partnerPropertyType);
        if (detected) {
          newFormData.locationTypeId = detected.locationTypeId;
          detectedTypeIdRef.current = detected.locationTypeId;
        }
      }
    }

    setFormData(newFormData);
    // Store initial state for pending changes detection (deep clone to avoid reference issues)
    savedFormDataRef.current = JSON.parse(JSON.stringify(newFormData)) as EnrichmentRequest;

    // Set owner override from existing enrichment
    const savedOwner = existingEnrichment?.owner ?? null;
    setOwnerOverride(savedOwner);
    savedOwnerOverrideRef.current = savedOwner;
  }, [existingEnrichment, getDefaultFormData, defaultCancellationPolicyId, defaultWorkHours, defaultInstantBooking, locationName, partnerPropertyType, taxonomy]);

  // Auto-clear invalid features when property type changes
  // This removes features that are not valid for the newly selected type
  useEffect(() => {
    // Only run when valid feature IDs are loaded (non-empty) and we have selected features
    // Empty array means no restrictions configured - don't clear anything
    if (!validFeatureIds || validFeatureIds.length === 0) return;
    const currentFeatures = formData.features ?? [];
    if (currentFeatures.length === 0) return;

    // Find features that are no longer valid for this type
    const validFeatures = currentFeatures.filter((id) => validFeatureIds.includes(id));

    // Only update if we actually removed something
    if (validFeatures.length < currentFeatures.length) {
      setFormData((prev) => ({ ...prev, features: validFeatures }));
    }
  }, [validFeatureIds, formData.features]);

  // Auto-clear invalid styles when property type changes
  // This removes styles that are not valid for the newly selected type
  useEffect(() => {
    // Only run when valid style IDs are loaded (non-empty) and we have selected styles
    // Empty array means no restrictions configured - don't clear anything
    if (!validStyleIds || validStyleIds.length === 0) return;
    const currentStyles = formData.styles ?? [];
    if (currentStyles.length === 0) return;

    // Find styles that are no longer valid for this type
    const validStyles = currentStyles.filter((id) => validStyleIds.includes(id));

    // Only update if we actually removed something
    if (validStyles.length < currentStyles.length) {
      setFormData((prev) => ({ ...prev, styles: validStyles }));
    }
  }, [validStyleIds, formData.styles]);

  // Track single-image reanalysis: set flag when mutation starts
  useEffect(() => {
    if (isReanalyzingSingleImage && reanalyzingImageIndex !== undefined) {
      singleImageReanalysisInProgress.current = true;
    }
  }, [isReanalyzingSingleImage, reanalyzingImageIndex]);

  // Clear single-image reanalysis flag when analysis completes
  useEffect(() => {
    const wasAnalyzing = previousTagAnalysisStatus.current === 'analyzing';
    const nowComplete = tagAnalysisStatus === 'completed' || tagAnalysisStatus === 'failed';

    if (wasAnalyzing && nowComplete && singleImageReanalysisInProgress.current) {
      singleImageReanalysisInProgress.current = false;
    }

    previousTagAnalysisStatus.current = tagAnalysisStatus;
  }, [tagAnalysisStatus]);

  // Compute the effective cancellation policy ID:
  // - Use formData value if explicitly set (including from existing enrichment)
  // - Always fall back to default "Standard 90 days" if no value set
  const effectiveCancellationPolicyId = useMemo(() => {
    if (formData.cancellationPolicyId) {
      return formData.cancellationPolicyId;
    }
    // Always fall back to default if available, even when existing enrichment has null
    // This handles the race condition where taxonomy loads after the initial render
    return defaultCancellationPolicyId;
  }, [formData.cancellationPolicyId, defaultCancellationPolicyId]);

  // Compute whether form has unsaved changes
  const hasPendingChanges = useMemo(() => {
    if (!savedFormDataRef.current) {
      // Form not yet initialized - no changes
      return false;
    }
    // Deep compare current form data with saved state
    const formDataChanged = JSON.stringify(formData) !== JSON.stringify(savedFormDataRef.current);
    // Also check owner override changes
    const ownerChanged = JSON.stringify(ownerOverride) !== JSON.stringify(savedOwnerOverrideRef.current);
    // Include tag changes (confirmed, rejected, or manually added/removed)
    return formDataChanged || ownerChanged || tagsChanged;
  }, [formData, ownerOverride, tagsChanged]);

  // Why Remove is unavailable on every card right now, if it is. A removal in
  // flight wins: it clears on its own, while unsaved edits need the admin.
  const removeDisabledReason = removingImageIndex !== undefined
    ? 'An image is being removed'
    : hasPendingChanges
      ? 'Save or discard your enrichment changes first'
      : undefined;

  // A run starts from the saved values, and its result reloads the form, which would drop
  // unsaved edits; so the form must be saved (or reset) first. A run on a location whose
  // category, styles and features were all set by hand could write nothing; the server refuses
  // it with the same words (R11).
  const allSetByHand =
    existingEnrichment?.typeSource === 'manual' &&
    existingEnrichment.stylesSource === 'manual' &&
    existingEnrichment.featuresSource === 'manual';
  const classifyDisabledReason = hasPendingChanges
    ? 'Save or discard your enrichment changes first'
    : allSetByHand
      ? 'Category, styles and features were all set by hand'
      : undefined;

  const handleClassify = (): void => {
    classifyLocation.mutate(syncedLocationId, {
      onSuccess: () => { setClassificationToSave(true); },
      onError: (error) => {
        const conflict = classifyConflictCode(error);
        if (conflict === 'ClassificationRunning') {
          toast({
            'data-testid': 'classify-already-running-toast',
            title: 'Classification already running',
            description: 'AI is already classifying this location. The result appears here when it is done.',
          });
          return;
        }
        if (conflict === 'NothingToClassify') {
          // R11, R16: a person set the category, styles and features; the server says so.
          toast({
            'data-testid': 'classify-nothing-to-do-toast',
            title: 'Nothing to classify',
            description: error.message,
          });
          return;
        }
        toast({
          'data-testid': 'classify-failed-to-start-toast',
          variant: 'destructive',
          title: 'Could not start classification',
          description: error instanceof Error ? error.message : 'Try again in a moment.',
        });
      },
    });
  };

  // Compute whether form meets minimum requirements for saving
  // Required: property category, property type, work hours, cancellation policy, max guests, min booking hours
  const canSave = Boolean(
    formData.locationTypeId &&
    (formData.workHours?.length ?? 0) > 0 &&
    effectiveCancellationPolicyId &&
    formData.minimumBookingHours != null && formData.minimumBookingHours >= defaultMinimumBookingHours
  );

  // Track which required fields are missing for the validation message
  const missingRequiredFields: string[] = [];
  if (!formData.locationTypeId) missingRequiredFields.push('Location Type');
  if ((formData.workHours?.length ?? 0) === 0) missingRequiredFields.push('Operating Hours');
  if (!effectiveCancellationPolicyId) missingRequiredFields.push('Cancellation Policy');
  if (formData.minimumBookingHours == null || formData.minimumBookingHours < defaultMinimumBookingHours) missingRequiredFields.push(`Minimum Hours (≥${String(defaultMinimumBookingHours)})`);

  const handleSubmit = (e: React.SyntheticEvent) => {
    e.preventDefault();

    // Show confirmation dialog for published locations
    if (isPublished) {
      setShowConfirmDialog(true);
      return;
    }

    // For unpublished locations, save directly
    void doSave();
  };

  const doSave = async () => {
    try {
      // Determine ownerEmail for the payload
      // - If ownerOverride is set, use its email
      // - If ownerOverride is null but savedOwnerOverrideRef was set, we're clearing it (send null)
      // - Otherwise, don't include ownerEmail (undefined = keep existing)
      let ownerEmail: string | null | undefined;
      if (ownerOverride) {
        ownerEmail = ownerOverride.email;
      } else if (savedOwnerOverrideRef.current !== null) {
        // ownerOverride is null/falsy and we had a saved override - clearing it
        ownerEmail = null;
      }
      // Otherwise, ownerEmail stays undefined (don't change existing value)

      // Prepare payload - exclude removed fields.
      // instantBooking flows through from formData, which is seeded from the
      // partner type default (catalogue partners like Podkarpackie: false).
      const payload: EnrichmentRequest = {
        ...formData,
        // Use effective cancellation policy (includes default if not explicitly set)
        cancellationPolicyId: effectiveCancellationPolicyId,
        // Explicitly exclude removed fields by not spreading them
        feeType: undefined,
        feeAmount: undefined,
        discount: undefined,
        security: undefined,
        blockedWeekdays: undefined,
        blockedWeekends: undefined,
        // Include owner email if changed
        ownerEmail,
        classificationBaseline: loadedClassificationRef.current,
        // Still the automatic pre-fill, with no subtype picked: saved as a guess AI may replace.
        categoryFromDetection:
          detectedTypeIdRef.current !== null &&
          formData.locationTypeId === detectedTypeIdRef.current &&
          (formData.subtypeIds ?? []).length === 0,
      };

      const result = await saveEnrichment.mutateAsync({
        syncedLocationId,
        data: payload,
      });

      // Reset saved state to current form data (no more pending changes)
      savedFormDataRef.current = JSON.parse(JSON.stringify(formData)) as EnrichmentRequest;
      // Until the reload re-initialises the form, a second save compares with what this one sent.
      loadedClassificationRef.current = {
        locationTypeId: formData.locationTypeId ?? null,
        subtypeIds: [...(formData.subtypeIds ?? [])],
        styles: [...(formData.styles ?? [])],
        features: [...(formData.features ?? [])],
      };
      savedOwnerOverrideRef.current = ownerOverride;
      // Reset tag change tracking (tags have been synced)
      setTagsChanged(false);
      setClassificationToSave(false);

      // Handle sync result: syncResult is non-null iff the location is
      // published (the API attempts a sync); syncedToScoutAround is false BOTH
      // when the sync failed and when nothing was pushed at all, so branching
      // on it alone showed a plain success toast for a failed/absent sync.
      if (result.syncResult?.success) {
        toast({
          'data-testid': 'enrichment-saved-synced-toast',
          title: 'Enrichment Saved & Synced',
          description: `Changes have been saved and synced to ScoutAround (${String(result.syncResult.updatedFields?.length ?? 0)} fields updated).`,
        });
      } else if (result.syncResult) {
        // Published, but the sync to ScoutAround failed
        toast({
          'data-testid': 'enrichment-saved-sync-failed-toast',
          title: 'Enrichment Saved (Sync Failed)',
          description: result.syncResult.error ?? 'Changes saved locally but failed to sync to ScoutAround.',
          variant: 'destructive',
        });
      } else {
        // Not published — nothing was pushed to the live listing
        toast({
          'data-testid': 'enrichment-saved-not-synced-toast',
          title: 'Enrichment Saved — Not Synced',
          description: 'This location is not published on ScoutAround, so the change is not live.',
        });
      }
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to save enrichment';
      toast({
        title: 'Error',
        description: message,
        variant: 'destructive',
      });
    }
  };

  if (isLoadingTaxonomy) {
    return (
      <div className="flex items-center justify-center py-8">
        <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
      </div>
    );
  }

  // Filter features based on valid IDs for the selected location type
  // If no location type is selected yet, or if valid relations return empty, show all features
  const allFeatureOptions = (taxonomy?.features ?? []).map((f) => ({
    value: f.id,
    label: f.name,
    group: f.category,
  }));
  // Only filter if we have a non-empty list of valid IDs
  // Empty array means no relations configured - show all options
  const filteredFeatureOptions = validFeatureIds && validFeatureIds.length > 0
    ? allFeatureOptions.filter((opt) => validFeatureIds.includes(opt.value))
    : allFeatureOptions;

  // Filter styles based on valid IDs for the selected location type
  // If no location type is selected yet, or if valid relations return empty, show all styles
  const allStyleOptions = (taxonomy?.styles ?? []).map((s) => ({
    value: s.id,
    label: s.name,
  }));
  // Only filter if we have a non-empty list of valid IDs
  // Empty array means no relations configured - show all options
  const filteredStyleOptions = validStyleIds && validStyleIds.length > 0
    ? allStyleOptions.filter((opt) => validStyleIds.includes(opt.value))
    : allStyleOptions;

  // Check if current selections contain invalid items (for user feedback)
  // Only check if we have a non-empty list of valid IDs (empty = no restrictions)
  const hasInvalidFeatures = validFeatureIds && validFeatureIds.length > 0 && (formData.features ?? []).some(
    (id) => !validFeatureIds.includes(id)
  );
  const hasInvalidStyles = validStyleIds && validStyleIds.length > 0 && (formData.styles ?? []).some(
    (id) => !validStyleIds.includes(id)
  );

  // Check if tag analysis is in progress (full analysis, not single-image)
  // Single-image reanalysis shows per-image spinner instead of blocking overlay
  const isAnalyzingTags = tagAnalysisStatus === 'analyzing' && !singleImageReanalysisInProgress.current;

  // The end of a classification run reloads the enrichment, which re-initialises the form and
  // would drop edits made meanwhile. So while a run is in flight everything below the category
  // fields (which lock themselves) is locked, Save included.
  const isLockedByClassification = isGeneratingType === true;

  return (
    <div className="relative">
      {/* Analyzing overlay - blocks form interaction during tag analysis */}
      {isAnalyzingTags && (
        <div
          className="absolute inset-0 z-20 flex flex-col items-center justify-center rounded-lg bg-background/80 backdrop-blur-sm"
          data-testid="analyzing-tags-overlay"
        >
          <Loader2 className="h-8 w-8 animate-spin text-primary mb-3" />
          <p className="text-sm font-medium text-primary">Analyzing tags with AI...</p>
          <p className="text-xs text-muted-foreground mt-1">
            This may take a few moments
          </p>
        </div>
      )}

      <form onSubmit={handleSubmit} className={`space-y-6 ${isAnalyzingTags ? 'pointer-events-none opacity-60' : ''}`}>
        {/* Location Type Selection */}
      <div>
      <TaxonomyTypeFields
        value={{ locationTypeId: formData.locationTypeId ?? null, subtypeIds: formData.subtypeIds ?? [] }}
        onChange={(next) => { setFormData((prev) => ({ ...prev, locationTypeId: next.locationTypeId, subtypeIds: next.subtypeIds })); }}
        locationTypes={taxonomy?.locationTypes ?? []}
        subtypes={subtypes ?? []}
        isLoadingSubtypes={isLoadingSubtypes}
        isGeneratingType={isGeneratingType}
        typeAiConfidence={typeAiConfidence}
        onClassify={handleClassify}
        isStartingClassification={classifyLocation.isPending}
        classifyDisabledReason={classifyDisabledReason}
        lastClassificationFailedAt={existingEnrichment?.typeClassificationFailedAt ?? null}
      />
      {/* Always mounted, so the reason is announced when it appears. */}
      <div role="status" aria-live="polite">
        {isLockedByClassification && (
          <p className="mt-3 text-sm text-muted-foreground" data-testid="classification-form-locked-notice">
            AI classification is queued or running. The form is locked until it finishes; the sections
            below still open for reading.
          </p>
        )}
      </div>
      </div>

      {/* disabled locks keyboard input too; the classes match the tag-analysis lock above. */}
      <fieldset
        disabled={isLockedByClassification}
        className={`min-w-0 space-y-6 ${isLockedByClassification ? 'pointer-events-none opacity-60' : ''}`}
        data-testid="enrichment-editable-fields"
      >
      {/* Styles Multi-Select - filtered by location type */}
      <div className="space-y-2">
        <MultiSelectField
          label="Styles"
          options={filteredStyleOptions}
          value={formData.styles ?? []}
          onChange={(styles) => { setFormData((prev) => ({ ...prev, styles })); }}
          placeholder={isLoadingValidStyles ? 'Loading valid styles...' : 'Select styles...'}
          testId="styles-multiselect"
          disabled={isLoadingValidStyles}
          labelAdornment={
            existingEnrichment?.stylesSource === 'ai' ? <AiAssignedHint testId="styles-source-badge" /> : undefined
          }
        />
        {formData.locationTypeId && !isLoadingValidStyles && (
          <p className="text-xs text-muted-foreground">
            {validStyleIds && validStyleIds.length > 0
              ? `Showing ${String(filteredStyleOptions.length)} styles available for the selected property type.`
              : `Showing all ${String(filteredStyleOptions.length)} styles (no type restrictions configured).`}
          </p>
        )}
        {hasInvalidStyles && (
          <p className="text-xs text-amber-600">
            Some selected styles are not valid for this property type and will be ignored when publishing.
          </p>
        )}
      </div>

      {/* Features Multi-Select - filtered by location type */}
      <div className="space-y-2">
        <MultiSelectField
          label="Features"
          options={filteredFeatureOptions}
          value={formData.features ?? []}
          onChange={(features) => { setFormData((prev) => ({ ...prev, features })); }}
          placeholder={isLoadingValidFeatures ? 'Loading valid features...' : 'Select features...'}
          testId="features-multiselect"
          disabled={isLoadingValidFeatures}
          labelAdornment={
            existingEnrichment?.featuresSource === 'ai' ? <AiAssignedHint testId="features-source-badge" /> : undefined
          }
        />
        {formData.locationTypeId && !isLoadingValidFeatures && (
          <p className="text-xs text-muted-foreground">
            {validFeatureIds && validFeatureIds.length > 0
              ? `Showing ${String(filteredFeatureOptions.length)} features available for the selected property type.`
              : `Showing all ${String(filteredFeatureOptions.length)} features (no type restrictions configured).`}
          </p>
        )}
        {hasInvalidFeatures && (
          <p className="text-xs text-amber-600">
            Some selected features are not valid for this property type and will be ignored when publishing.
          </p>
        )}
      </div>

      {/* Activities Multi-Select */}
      <MultiSelectField
        label="Activities Allowed"
        options={(taxonomy?.activities ?? []).map((a) => ({ value: a.id, label: a.name }))}
        value={formData.activities ?? []}
        onChange={(activities) => { setFormData((prev) => ({ ...prev, activities })); }}
        placeholder="Select activities..."
        testId="activities-multiselect"
      />

      {/* Rules Multi-Select */}
      <MultiSelectField
        label="Location Rules"
        options={(taxonomy?.rules ?? []).map((r) => ({ value: r.id, label: r.name }))}
        value={formData.rules ?? []}
        onChange={(rules) => { setFormData((prev) => ({ ...prev, rules })); }}
        placeholder="Select rules..."
        testId="rules-multiselect"
      />

      {/* Access Types Multi-Select */}
      <MultiSelectField
        label="Access Types"
        options={(taxonomy?.accessTypes ?? []).map((a) => ({ value: a.id, label: a.name }))}
        value={formData.accesses ?? []}
        onChange={(accesses) => { setFormData((prev) => ({ ...prev, accesses })); }}
        placeholder="Select access types..."
        testId="accesses-multiselect"
      />

      {/* Parking Options */}
      <ParkingSelector
        value={formData.parking ?? []}
        onChange={(parking) => { setFormData((prev) => ({ ...prev, parking })); }}
        parkingTypes={taxonomy?.parkingTypes ?? []}
        parkingAttributes={taxonomy?.parkingAttributes ?? []}
      />

      {/* Cancellation Policy */}
      <div className="space-y-2">
        <Label htmlFor="cancellationPolicy" className="flex items-center gap-2">
          Cancellation Policy
          <span className="text-xs text-destructive">*</span>
        </Label>
        {partnerCancellationPolicy && (
          <div className="rounded-md border border-blue-200 bg-blue-50/50 px-3 py-2 text-sm">
            <span className="font-medium text-blue-700">Partner policy:</span>{' '}
            <span className="text-blue-600">{partnerCancellationPolicy}</span>
            <p className="mt-1 text-xs text-blue-500">
              Select the ScoutAround policy that best matches this partner policy.
            </p>
          </div>
        )}
        <Select
          value={effectiveCancellationPolicyId ?? 'none'}
          onValueChange={(value) => {
            setFormData((prev) => ({
              ...prev,
              cancellationPolicyId: value === 'none' ? null : value,
            }));
          }}
        >
          <SelectTrigger
            id="cancellationPolicy"
            data-testid="cancellation-policy-select"
            className={!effectiveCancellationPolicyId ? 'border-amber-300' : ''}
          >
            <SelectValue placeholder="Select cancellation policy (required)" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="none" disabled>Select a policy...</SelectItem>
            {(taxonomy?.cancellationPolicies ?? []).map((policy) => (
              <SelectItem key={policy.id} value={policy.id}>
                {policy.title}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        {!effectiveCancellationPolicyId && (
          <p className="text-xs text-amber-600">
            A cancellation policy is required before publishing.
          </p>
        )}
      </div>

      {/* Capacity & Booking Settings */}
      <div className="grid gap-4 md:grid-cols-2">
        <div className="space-y-2">
          <Label htmlFor="maximumGuests" className="flex items-center gap-2">
            Max Crew
            <span className="text-xs text-muted-foreground">(optional)</span>
          </Label>
          <Input
            id="maximumGuests"
            type="number"
            min={1}
            value={formData.maximumGuests ?? ''}
            onChange={(e) => {
              setFormData((prev) => ({
                ...prev,
                maximumGuests: e.target.value ? parseInt(e.target.value, 10) : null,
              }));
            }}
            placeholder="e.g., 50"
            data-testid="max-guests-input"
          />
        </div>

        <div className="space-y-2">
          <Label htmlFor="minimumBookingHours" className="flex items-center gap-2">
            Minimum Hours per Booking
            <span className="text-xs text-destructive">*</span>
            <span className="text-xs text-muted-foreground">(minimum {String(defaultMinimumBookingHours)})</span>
          </Label>
          <Input
            id="minimumBookingHours"
            type="number"
            min={defaultMinimumBookingHours}
            value={formData.minimumBookingHours ?? ''}
            onChange={(e) => {
              const value = e.target.value ? parseInt(e.target.value, 10) : null;
              setFormData((prev) => ({
                ...prev,
                minimumBookingHours: value != null && value < defaultMinimumBookingHours ? defaultMinimumBookingHours : value,
              }));
            }}
            placeholder={`e.g., ${String(defaultMinimumBookingHours)}`}
            data-testid="min-hours-input"
          />
        </div>
      </div>

      {/* Location Tags Section */}
      <LocationTagsSection
        locationId={syncedLocationId}
        onTagsChange={() => { setTagsChanged(true); }}
      />

      {/* Unified Image Grid with Cover Photo + Tags */}
      {partnerImages.length > 0 && (
        <UnifiedImageGrid
          images={partnerImages}
          selectedCoverOrder={formData.coverImageIndex ?? null}
          onCoverChange={(order) => { setFormData((prev) => ({ ...prev, coverImageIndex: order })); }}
          confirmedImageTags={tagData?.confirmedImageTags ?? {}}
          manualImageTags={tagData?.manualImageTags}
          imageSuggestions={tagData?.imageSuggestions ?? {}}
          imageRuns={tagData?.imageRuns}
          allSuggestions={tagData?.allSuggestions ?? {}}
          availableTags={tagData?.availableTags}
          rejectedTags={tagData?.rejectedTags ?? []}
          processingTags={tagData?.processingTags ?? new Set()}
          removingTags={tagData?.removingTags}
          onAcceptTag={(tagId, imageIndex) => {
            setTagsChanged(true);
            onAcceptTag?.(tagId, imageIndex);
          }}
          onRejectTag={(tagId, imageIndex) => {
            setTagsChanged(true);
            onRejectTag?.(tagId, imageIndex);
          }}
          onRemoveTag={onRemoveTag ? (tagId, imageIndex) => {
            setTagsChanged(true);
            onRemoveTag(tagId, imageIndex);
          } : undefined}
          onImageView={onImageView}
          disabled={saveEnrichment.isPending || removingImageIndex !== undefined || isLockedByClassification}
          locationId={syncedLocationId}
          onTagAdded={() => { setTagsChanged(true); }}
          onReanalyzeAllImages={onReanalyzeAllImages}
          isReanalyzingAllImages={isReanalyzingAllImages}
          onReanalyzeSingleImage={onReanalyzeSingleImage}
          isReanalyzingSingleImage={isReanalyzingSingleImage}
          reanalyzingImageIndex={reanalyzingImageIndex}
          onRemoveImage={onRemoveImage}
          removingImageIndex={removingImageIndex}
          removeDisabledReason={removeDisabledReason}
          onRemoveImages={onRemoveImages}
          bulkRemovalProgress={bulkRemovalProgress}
        />
      )}

      </fieldset>

      {/* Collapsible Sections: outside the locked fieldset, so their headers still open them for
          reading while a run is in flight; their controls lock with contentDisabled. */}
      {/* Operating Hours */}
      <CollapsibleSection
        title="Operating Hours"
        icon={<Clock className="h-4 w-4 text-muted-foreground" />}
        defaultCollapsed
        contentDisabled={isLockedByClassification}
        testId="operating-hours-section"
      >
        <WorkHoursEditor
          value={formData.workHours ?? []}
          onChange={(workHours) => { setFormData((prev) => ({ ...prev, workHours })); }}
          required
        />
      </CollapsibleSection>

      {/* Owner Override Section */}
      <CollapsibleSection
        title="Location Owner"
        icon={<User className="h-4 w-4 text-muted-foreground" />}
        defaultCollapsed
        badge={ownerOverride ? (
          <span className="text-xs text-green-600 font-normal">(override active)</span>
        ) : undefined}
        contentDisabled={isLockedByClassification}
        testId="owner-section"
      >
        <div className="space-y-3">
          <p className="text-sm text-muted-foreground">
            When this location is published, it will be owned by this user in ScoutAround.
          </p>

          {/* Show partner default owner */}
          {partnerDefaultOwner && (
            <div className="rounded-md border border-blue-200 bg-blue-50/50 px-3 py-2 text-sm">
              <span className="font-medium text-blue-700">Partner default:</span>{' '}
              <span className="text-blue-600">
                {partnerDefaultOwner.name !== partnerDefaultOwner.email
                  ? `${partnerDefaultOwner.name} (${partnerDefaultOwner.email})`
                  : partnerDefaultOwner.email}
              </span>
              {!ownerOverride && (
                <p className="mt-1 text-xs text-blue-500">
                  This user will be set as the owner when the location is published.
                </p>
              )}
            </div>
          )}

          {!partnerDefaultOwner && !ownerOverride && (
            <div className="rounded-md border border-amber-200 bg-amber-50/50 px-3 py-2 text-sm">
              <span className="font-medium text-amber-700">No default owner configured</span>
              <p className="mt-1 text-xs text-amber-600">
                Set an owner override below, or configure a default owner on the partner account.
              </p>
            </div>
          )}

          <div className="space-y-2">
            <Label htmlFor="owner-override">
              Override Owner {ownerOverride && <span className="text-xs text-muted-foreground">(active)</span>}
            </Label>
            <UserAutocomplete
              value={ownerOverride}
              onChange={setOwnerOverride}
              placeholder="Search user by email to override..."
              data-testid="owner-override"
            />
            {ownerOverride && (
              <p className="text-xs text-green-600">
                This override will be used instead of the partner default when publishing.
              </p>
            )}
          </div>
        </div>
      </CollapsibleSection>

      {/* Pricing */}
      <CollapsibleSection
        title="Pricing"
        icon={<Euro className="h-4 w-4 text-muted-foreground" />}
        defaultCollapsed
        contentDisabled={isLockedByClassification}
        testId="pricing-section"
      >
        <PricingFields
          pricePerHour={formData.pricePerHour ?? undefined}
          pricePerDay={formData.pricePerDay ?? undefined}
          pricePerWeek={formData.pricePerWeek ?? undefined}
          currency={formData.currency === undefined ? 'EUR' : formData.currency}
          priceOnRequest={formData.priceOnRequest ?? false}
          onChange={(pricing) => { setFormData((prev) => ({ ...prev, ...pricing })); }}
          readOnly={pricingFormulaEnabled}
        />
      </CollapsibleSection>

      {/* Action Buttons */}
      <div className="flex items-center justify-end gap-4">
        {!canSave && missingRequiredFields.length > 0 && (
          <span className="text-sm text-destructive">
            Missing: {missingRequiredFields.join(', ')}
          </span>
        )}
        <Button type="submit" disabled={saveEnrichment.isPending || !(hasPendingChanges || classificationToSave) || !canSave || isLockedByClassification} data-testid="save-enrichment-btn">
          {saveEnrichment.isPending ? (
            <Loader2 className="mr-2 h-4 w-4 animate-spin" />
          ) : isPublished ? (
            <CloudUpload className="mr-2 h-4 w-4" />
          ) : (
            <Save className="mr-2 h-4 w-4" />
          )}
          {isPublished ? 'Save & Sync to ScoutAround' : 'Save Enrichment'}
        </Button>
      </div>

      {/* Confirmation Dialog for Published Locations */}
      <AlertDialog open={showConfirmDialog} onOpenChange={setShowConfirmDialog}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Sync Changes to ScoutAround?</AlertDialogTitle>
            <AlertDialogDescription>
              This location is published on ScoutAround. Saving these changes will automatically
              sync them to the live listing. The changes will be visible immediately.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => {
                setShowConfirmDialog(false);
                void doSave();
              }}
              data-testid="confirm-sync-btn"
            >
              Save & Sync
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
      </form>
    </div>
  );
}

/**
 * Marks a field whose stored value a classification run chose, worded like the type's
 * "AI-assigned" line in TaxonomyTypeFields. A person's save that changes the field makes it
 * theirs (D11), and AI never replaces it after that.
 */
function AiAssignedHint({ testId }: { testId: string }): JSX.Element {
  return (
    <span
      data-testid={testId}
      className="text-xs font-normal text-muted-foreground"
      title="Chosen by AI. If you change it and save, AI will not change it again."
    >
      AI-assigned
    </span>
  );
}
